import React, { useEffect } from 'react';
import { IoWarningOutline, IoClose } from 'react-icons/io5';

/**
 * Reusable confirm modal (window.confirm ka replacement).
 *
 * <ConfirmModal
 *    isOpen={open}
 *    onClose={() => setOpen(false)}
 *    onConfirm={handleDelete}
 *    title="Remove all favorites?"
 *    message="Ye saare saved doctors hat jayenge."
 *    confirmText="Clear all"
 *    tone="danger"
 * />
 */
export default function ConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title = 'Are you sure?',
    message = '',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    tone = 'danger', // 'danger' | 'brand'
    icon,
    loading = false,
}) {
    // Escape se band + background scroll lock
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const toneMap = {
        danger: {
            iconWrap: 'bg-red-50 text-red-500',
            btn: 'bg-gradient-to-b from-red-500 to-red-600 shadow-md hover:brightness-105',
        },
        brand: {
            iconWrap: 'bg-brand-50 text-brand-600',
            btn: 'bg-gradient-to-b from-brand-400 to-brand-500 shadow-brand hover:brightness-105',
        },
    };
    const t = toneMap[tone] || toneMap.danger;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
            role="dialog"
            aria-modal="true"
        >
            {/* backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]"
                onClick={loading ? undefined : onClose}
            />

            {/* card - mobile par bottom sheet, sm+ par centered */}
            <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl p-5 sm:p-6 animate-[slideUp_0.25s_ease-out] sm:animate-[fadeIn_0.2s_ease-out]">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="absolute top-3 right-3 grid place-items-center h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition disabled:opacity-40"
                    aria-label="Close"
                >
                    <IoClose size={20} />
                </button>

                <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
                    <span className={`grid place-items-center h-14 w-14 rounded-full mb-4 ${t.iconWrap}`}>
                        {icon || <IoWarningOutline size={28} />}
                    </span>
                    <h2 className="text-lg font-bold text-gray-800">{title}</h2>
                    {message && <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{message}</p>}
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition active:scale-95 disabled:opacity-40"
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        className={`px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition active:scale-95 disabled:opacity-60 ${t.btn}`}
                    >
                        {loading ? 'Please wait...' : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}